import React from "react";
import {
  Box,
  FormControl,
  Autocomplete,
  TextField
} from "@mui/material";
import PropTypes from "prop-types";
import ClearIcon from '@mui/icons-material/Clear';

/**
 * CDropdown - Enhanced dropdown with sessionStorage support.
 */
const CDropdown = ({
  selectedValue,
  onValueChange,
  options = [],
  label = "Select Option",
  sx = {},
  theme,
  showSearch = false,
  clearable = true,
  disabled = false,
  required = false,
  sessionKey = "",
  sessionField = "",
  useSessionStorage = false,
  ...rest
}) => {
  // Safe options
  const safeOptions = Array.isArray(options) ? options : [];

  // Resolve selectedValue using sessionStorage if enabled
  let effectiveSelectedValue = selectedValue;
  if (useSessionStorage && sessionKey && sessionField) {
    try {
      const storedData = sessionStorage.getItem(sessionKey);
      if (storedData) {
        const parsed = JSON.parse(storedData);
        if (parsed[sessionField] !== undefined) {
          effectiveSelectedValue = parsed[sessionField];
        }
      }
    } catch (error) {
      console.error("Error reading dropdown session storage:", error);
    }
  }

  const normalizedSelectedValue = effectiveSelectedValue !== null && effectiveSelectedValue !== undefined
    ? String(effectiveSelectedValue)
    : '';

  const selectedOption = safeOptions.find(option => {
    if (!option) return false;
    const optionValue = option.value !== undefined ? option.value : 
                       (option.name !== undefined ? option.name : 
                       (option.label !== undefined ? option.label : null));
    return optionValue !== null && optionValue !== undefined &&
      normalizedSelectedValue !== '' &&
      String(optionValue) === normalizedSelectedValue;
  }) || null;

  const handleChange = (event, newValue) => {
    // Initialize with empty string
    let selectedOptionValue = '';

    // Safely extract the value from newValue if it exists
    if (newValue !== null && newValue !== undefined) {
      // Check if any of these properties exist before trying to access them
      selectedOptionValue = newValue.value !== undefined ? newValue.value :
                          (newValue.name !== undefined ? newValue.name :
                          (newValue.label !== undefined ? newValue.label : ''));
    }

    // Handle sessionStorage update/removal
    if (useSessionStorage && sessionKey && sessionField) {
      try {
        let data = {};
        const existing = sessionStorage.getItem(sessionKey);
        if (existing) {
          data = JSON.parse(existing);
        }
        
        if (newValue === null) {
          // If user cleared the input (clicked the X), remove from sessionStorage
          if (data[sessionField] !== undefined) {
            delete data[sessionField];
            console.log(`Field ${sessionField} cleared from sessionStorage.`);
          }
        } else {
          // Store the raw value in sessionStorage
          data[sessionField] = selectedOptionValue;
        }
        
        sessionStorage.setItem(sessionKey, JSON.stringify(data));
      } catch (error) {
        console.error("Failed to update dropdown in sessionStorage:", error);
      }
    }

    // Call the parent's onValueChange with the raw value directly
    // This ensures consistency between state and sessionStorage
    onValueChange(selectedOptionValue);
  };

  // Compact styles with small font size and centered arrow
  const compactStyles = {
    // Keep full width for grid arrangement
    width: '100%',
    
    '& .MuiAutocomplete-root': {
      fontSize: '1rem',
    },
    
    '& .MuiAutocomplete-inputRoot': {
      minHeight: '40px',
      height: '40px',
      borderRadius: '4px',
      paddingTop: '0!important',
      paddingBottom: '0!important',
      fontSize: '1rem',
      padding: '0',
      
      '& .MuiInputBase-input': {
        paddingTop: '8px',
        paddingBottom: '8px',
        fontSize: '1rem',
        height: '40px',
        lineHeight: '1.2',
      },
      
      // Center align the dropdown arrow
      '& .MuiAutocomplete-endAdornment': {
        position: 'absolute',
        right: '8px',
        top: '50%',
        transform: 'translateY(-50%)',
        
        '& .MuiAutocomplete-popupIndicator': {
          padding: '2px',
          
          '& .MuiSvgIcon-root': {
            fontSize: '16px',
          }
        },
        
        '& .MuiAutocomplete-clearIndicator': {
          padding: '2px',
          marginRight: '2px',
          
          '& .MuiSvgIcon-root': {
            fontSize: '14px',
          }
        }
      }
    },
    
    // Style the dropdown options
    '& .MuiAutocomplete-listbox': {
      fontSize: '1rem',
      
      '& .MuiAutocomplete-option': {
        fontSize: '1rem',
        minHeight: '40px',
        padding: '8px 8px',
      }
    }
  };

  // Combine compact styles with user-provided styles
  const combinedStyles = {
    ...compactStyles,
    ...sx,
  };

  return (
    <Box sx={{ mb: 1, ...combinedStyles }}>
      <FormControl
        variant="outlined"
        size="small"
        sx={{ width: 'inherit', minWidth: 'inherit', maxWidth: 'inherit' }}
        disabled={disabled}
        required={required}
        {...rest}
      >
        <Autocomplete
          id={`dropdown-${typeof label === 'string' ? label.replace(/\s+/g, '-').toLowerCase() : 'custom'}`}
          value={selectedOption}
          onChange={handleChange}
          options={safeOptions}
          fullWidth
          getOptionLabel={(option) => {
            if (!option) return '';
            return String(option.label ?? option.name ?? option.value ?? '');
          }}
          isOptionEqualToValue={(option, value) => {
            if (!option || !value) return false;
            const optionValue = option.value !== undefined ? option.value : 
                               (option.name !== undefined ? option.name : 
                               (option.label !== undefined ? option.label : null));
            const valueValue = value.value !== undefined ? value.value : 
                              (value.name !== undefined ? value.name : 
                              (value.label !== undefined ? value.label : null));
            return optionValue !== null && valueValue !== null && 
                   optionValue !== undefined && valueValue !== undefined && 
                   String(optionValue) === String(valueValue);
          }}
          disabled={disabled}
          disableClearable={!clearable}
          size="small"
          clearIcon={clearable ? <ClearIcon fontSize="small" /> : null}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              variant="outlined"
              size="small"
              required={required}
              InputLabelProps={{
                sx: {
                  fontSize: '1rem',
                  '&.Mui-focused': {
                    fontSize: '1rem',
                  },
                  '&.MuiInputLabel-shrink': {
                    fontSize: '1rem',
                  },
                  '&.Mui-required': {
                    '& .MuiFormLabel-asterisk': {
                      color: 'red',
                      fontSize: '0.75rem'
                    }
                  }
                }
              }}
              sx={{
                height: '40px',
                
                '& .MuiInputBase-root': {
                  height: '40px',
                  fontSize: '1rem',
                  padding: '0'
                },
                
                '& .MuiInputBase-input': {
                  height: '40px',
                  padding: '8px 8px',
                  fontSize: '1rem',
                  lineHeight: '1.2',
                },
                
                '& .MuiOutlinedInput-root': {
                  height: '40px'
                },
                
                '& .MuiOutlinedInput-notchedOutline': {
                  borderRadius: '4px',
                }
              }}
            />
          )}
          noOptionsText={safeOptions.length > 0 ? "No options found" : "No options available"}
          openOnFocus={!showSearch}
          disablePortal={false}
          autoHighlight
          autoComplete
          slotProps={{
            popper: {
              sx: {
                zIndex: 1300,
              }
            }
          }}
          sx={{
            '& .MuiAutocomplete-inputRoot': {
              height: '40px',
              paddingTop: '0!important',
              paddingBottom: '0!important',
              fontSize: '1rem',
            }
          }}
        />
      </FormControl>
    </Box>
  );
};

CDropdown.propTypes = {
  selectedValue: PropTypes.any,
  onValueChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.any,
      value: PropTypes.any,
      label: PropTypes.any
    })
  ),
  label: PropTypes.string,
  sx: PropTypes.object,
  theme: PropTypes.object,
  showSearch: PropTypes.bool,
  clearable: PropTypes.bool,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  sessionKey: PropTypes.string,
  sessionField: PropTypes.string,
  useSessionStorage: PropTypes.bool
};

export default CDropdown;